import { Controller } from "@hotwired/stimulus";

// Listens for postMessage events from the preview iframe (sent by the
// editable-custom-text controller). On `customText:edit` it loads the
// edit form into the string-inspector turbo-frame and toggles an
// `editing-string` class on the inspector — CSS hides the regular
// tab panels and reveals the frame, so the existing tab strip stays
// untouched. On `customText:deselect`, on a click outside the inspector
// (excluding the iframe and dropdowns), or on a click on any inspector
// tab, the editor closes.
export default class extends Controller {
  static values = {
    previewOrigin: String,
    editUrlTemplate: String,
    frame: String,
  };

  connect() {
    this.selectedKey = null;
    this.selectedTemplateClass = null;
    window.addEventListener("message", this.#onMessage);
    document.addEventListener("click", this.#onDocumentClick);
    // After every save/delete from the inspector form, tell preview iframes
    // to reload. Belt-and-braces with the server-side broadcast_refresh — the
    // ActionCable subscription can miss a broadcast if it fires before the
    // iframe's subscription is established (intermittent, just-loaded races).
    document.addEventListener(
      "turbo:submit-end",
      this.#onInspectorSubmitEnd,
      true,
    );
  }

  disconnect() {
    window.removeEventListener("message", this.#onMessage);
    document.removeEventListener("click", this.#onDocumentClick);
    document.removeEventListener(
      "turbo:submit-end",
      this.#onInspectorSubmitEnd,
      true,
    );
  }

  close() {
    this.#closeEditor();
  }

  #onInspectorSubmitEnd = (event) => {
    // Only react to forms inside the string inspector frame (the edit form
    // and the Reset link's Turbo-method DELETE both target it).
    const frame = document.getElementById(this.frameValue);
    if (!frame || !frame.contains(event.target)) return;
    if (!event.detail?.success) return;
    this.#postToAllIframes({ kind: "customText:reloadPreview" });
  };

  #onMessage = (event) => {
    if (event.origin !== this.previewOriginValue) return;
    const data = event.data;
    if (data?.kind === "customText:edit")
      this.#openEditor(data.key, data.templateClass, data.templateKey);
    if (data?.kind === "customText:deselect") this.#closeEditor();
    if (data?.kind === "customText:ready")
      this.#onIframeReady(data.key, data.templateClass, event.source);
  };

  #onDocumentClick = (event) => {
    if (!this.#isEditing) return;
    if (event.target.closest('[data-inspector-tabs-target="tab"]')) {
      this.#closeEditor();
      return;
    }
    if (this.element.contains(event.target)) return;
    if (event.target.closest("iframe")) return;
    if (event.target.closest("[data-controller~='dropdown']")) return;
    this.#closeEditor();
  };

  #openEditor(key, templateClass, templateKey) {
    if (!key || !templateClass || !templateKey) return;
    this.selectedKey = key;
    this.selectedTemplateClass = templateClass;

    const frame = document.getElementById(this.frameValue);
    if (frame) {
      frame.src = this.editUrlTemplateValue
        .replace("__TEMPLATE_KEY__", encodeURIComponent(templateKey))
        .replace("__KEY__", encodeURIComponent(key));
    }

    this.element.classList.add("editing-string");
  }

  #closeEditor() {
    this.selectedKey = null;
    this.selectedTemplateClass = null;
    this.element.classList.remove("editing-string");

    const frame = document.getElementById(this.frameValue);
    if (frame) {
      frame.removeAttribute("src");
      frame.innerHTML = "";
    }

    this.#postToAllIframes({ kind: "customText:clearSelection" });
  }

  #onIframeReady(key, templateClass, source) {
    if (!this.selectedKey || this.selectedKey !== key) return;
    if (this.selectedTemplateClass !== templateClass) return;
    if (!source) return;
    source.postMessage(
      { kind: "customText:select", key, templateClass },
      this.previewOriginValue,
    );
  }

  #postToAllIframes(message) {
    // Only target iframes actually loaded from the preview origin. The
    // browser would drop a cross-origin postMessage anyway because we pass
    // `this.previewOriginValue` as targetOrigin, but pre-filtering avoids
    // dispatching to unrelated iframes (admin widgets, third-party embeds)
    // every time the inspector saves.
    document.querySelectorAll("iframe").forEach((iframe) => {
      if (!iframe.src?.startsWith(this.previewOriginValue)) return;
      iframe.contentWindow?.postMessage(message, this.previewOriginValue);
    });
  }

  get #isEditing() {
    return this.element.classList.contains("editing-string");
  }
}
